import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { CircleHelp, Eye, EyeOff, Menu, MessageCircleMore, Moon, Search as SearchIcon, Sun } from 'lucide-react';
import NotificationsMenu from '../Navbar/NotificationsMenu.jsx';
import UserMenu from '../Navbar/UserMenu.jsx';
import IconButton from '../Navbar/IconButton.jsx';
import { useTheme } from '../../theme/ThemeContext.jsx';
import { useUI } from '../../context/UIContext.jsx';
import SearchGuidesModal from '../Search/SearchGuidesModal.jsx';
import SearchClientsModal from '../Search/SearchClientsModal.jsx';
import { fetchChatBootstrap } from '@/services/chat.js';
import { useHelp } from '@/help/HelpProvider.jsx';
import { useOperatorCompactRoute } from '@/utils/operatorCompact.js';
import { readSinaHidden, setSinaHidden, SINA_VISIBILITY_EVENT } from '../SinaAssistant/sinaPreferences.js';

function useCompactHeight() {
  const [isCompactHeight, setIsCompactHeight] = useState(() => {
    if (typeof window === 'undefined') {
      return false;
    }
    return window.innerHeight < 700;
  });

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }

    const sync = () => setIsCompactHeight(window.innerHeight < 700);
    sync();
    window.addEventListener('resize', sync);

    return () => {
      window.removeEventListener('resize', sync);
    };
  }, []);

  return isCompactHeight;
}

export default function Topbar() {
  const { theme, toggleTheme } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const { isCompactViewport, isSidebarHoverOpen, isSidebarPinned, toggleSidebar, layoutSettings } = useUI();
  const { openHelp } = useHelp();
  const [isSinaHidden, setIsSinaHidden] = useState(() => readSinaHidden());

  useEffect(() => {
    const syncSinaVisibility = () => setIsSinaHidden(readSinaHidden());
    window.addEventListener(SINA_VISIBILITY_EVENT, syncSinaVisibility);
    return () => window.removeEventListener(SINA_VISIBILITY_EVENT, syncSinaVisibility);
  }, []);
  const [isGuidesModalOpen, setIsGuidesModalOpen] = useState(false);
  const [isClientsModalOpen, setIsClientsModalOpen] = useState(false);
  const [isRevealed, setIsRevealed] = useState(false);
  const [chatUnreadCount, setChatUnreadCount] = useState(0);
  const isCompactHeight = useCompactHeight();
  const { isOperatorCompact } = useOperatorCompactRoute();
  const isEmissionView = location.pathname === '/guias/emitir';
  const shouldShowSidebarButton = isCompactViewport || !isSidebarPinned;
  const topbarHeightClass = isOperatorCompact ? 'h-11' : isCompactHeight || isEmissionView ? 'h-12' : 'h-[var(--topbar-h)]';
  const topbarInnerClassName = isOperatorCompact ? 'gap-2 px-2 md:px-3' : 'gap-3 px-3 md:px-4';
  const topbarLeftGapClassName = isOperatorCompact ? 'gap-1.5 md:gap-2' : 'gap-2 md:gap-3';
  const topbarRightGapClassName = isOperatorCompact ? 'gap-1.5' : 'gap-2';
  const quickActionClassName = isOperatorCompact
    ? 'hidden min-w-0 items-center gap-1.5 rounded-lg border border-[rgba(var(--border),0.6)] bg-[rgb(var(--panel))] px-2 py-1 text-[11px] text-[rgb(var(--fg))] shadow-[0_6px_14px_rgba(var(--shadow),0.08)] transition hover:bg-[rgba(var(--subpanel),0.92)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))] md:inline-flex'
    : 'hidden min-w-0 items-center gap-2 rounded-xl border border-[rgba(var(--border),0.6)] bg-[rgb(var(--panel))] px-2.5 py-1.5 text-[12px] text-[rgb(var(--fg))] shadow-[0_8px_18px_rgba(var(--shadow),0.1)] transition hover:bg-[rgba(var(--subpanel),0.92)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))] md:inline-flex';
  const iconButtonClassName = isOperatorCompact
    ? 'rounded-lg border border-[rgba(var(--border),0.6)] bg-[rgb(var(--panel))] text-[rgb(var(--fg))] shadow-[0_6px_14px_rgba(var(--shadow),0.08)] hover:bg-[rgba(var(--subpanel),0.92)]'
    : 'rounded-xl border border-[rgba(var(--border),0.6)] bg-[rgb(var(--panel))] text-[rgb(var(--fg))] shadow-[0_10px_24px_rgba(var(--shadow),0.12)] hover:bg-[rgba(var(--subpanel),0.92)]';

  const shouldAutoHide = useMemo(
    () => Boolean(layoutSettings.autoHideNavbar && isCompactHeight),
    [isCompactHeight, layoutSettings.autoHideNavbar],
  );

  useEffect(() => {
    if (!shouldAutoHide) {
      setIsRevealed(true);
      return undefined;
    }

    setIsRevealed(false);

    const handlePointerMove = event => {
      if (event.clientY <= 8) {
        setIsRevealed(true);
        return;
      }

      if (event.clientY > 96) {
        setIsRevealed(false);
      }
    };

    window.addEventListener('mousemove', handlePointerMove);
    return () => {
      window.removeEventListener('mousemove', handlePointerMove);
    };
  }, [shouldAutoHide]);

  useEffect(() => {
    let mounted = true;

    const syncUnread = async () => {
      try {
        const bootstrap = await fetchChatBootstrap();
        if (!mounted || bootstrap?.chatUnavailable) return;
        const totalUnread = Array.isArray(bootstrap?.conversations)
          ? bootstrap.conversations.reduce((acc, item) => acc + Number(item?.unreadCount || 0), 0)
          : 0;
        setChatUnreadCount(totalUnread);
      } catch {
        if (mounted) {
          setChatUnreadCount(0);
        }
      }
    };

    syncUnread();
    const intervalId = window.setInterval(syncUnread, 10000);

    return () => {
      mounted = false;
      window.clearInterval(intervalId);
    };
  }, []);

  const showTopbar = !shouldAutoHide || isRevealed;
  const quickActions = (
    <>
      <button
        type="button"
        className={quickActionClassName}
        onClick={() => setIsGuidesModalOpen(true)}
      >
        <SearchIcon size={15} className="text-[rgba(var(--muted),0.9)]" aria-hidden="true" />
        <span className="hidden lg:inline">{'Gu\u00edas r\u00e1pidas'}</span>
        <span className="lg:hidden">{'Gu\u00edas'}</span>
      </button>

      <button
        type="button"
        className={quickActionClassName}
        onClick={() => setIsClientsModalOpen(true)}
      >
        <SearchIcon size={15} className="text-[rgba(var(--muted),0.9)]" aria-hidden="true" />
        <span className="hidden lg:inline">{'B\u00fasqueda de clientes'}</span>
        <span className="lg:hidden">Clientes</span>
      </button>

      <button
        type="button"
        className={isOperatorCompact ? `${quickActionClassName} inline-flex md:inline-flex` : 'inline-flex min-w-0 items-center gap-2 rounded-xl border border-[rgba(var(--border),0.6)] bg-[rgb(var(--panel))] px-2.5 py-1.5 text-[12px] text-[rgb(var(--fg))] shadow-[0_8px_18px_rgba(var(--shadow),0.1)] transition hover:bg-[rgba(var(--subpanel),0.92)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]'}
        onClick={openHelp}
        title="Abrir ayuda contextual (F1)"
      >
        <CircleHelp size={15} className="text-[rgba(var(--muted),0.9)]" aria-hidden="true" />
        <span>Ayuda</span>
        <span className="hidden rounded-full border border-[rgba(var(--border),0.55)] px-1.5 py-0.5 text-[10px] font-semibold text-[rgba(var(--muted),0.95)] md:inline-flex">
          F1
        </span>
      </button>
    </>
  );

  return (
    <>
      <div className={showTopbar ? `relative ${topbarHeightClass} transition-[height] duration-200` : 'relative h-0 transition-[height] duration-200'}>
        <header
          className={`navbar absolute inset-x-0 top-0 ${topbarHeightClass} transition-all duration-200 ${
            showTopbar ? 'translate-y-0 opacity-100' : '-translate-y-full opacity-0 pointer-events-none'
          }`}
          aria-label="Barra superior"
          data-qa-topbar="true"
        >
          <div className={`mx-auto flex h-full w-full max-w-[var(--content-max)] items-center justify-between ${topbarInnerClassName}`}>
            <div className={`flex min-w-0 items-center ${topbarLeftGapClassName}`}>
              <button
                type="button"
                className={`flex ${isOperatorCompact ? 'h-8 w-8 rounded-lg' : 'h-9 w-9 rounded-xl'} items-center justify-center border border-[rgba(var(--border),0.6)] bg-[rgb(var(--panel))] text-[rgb(var(--fg))] shadow-[0_10px_24px_rgba(var(--shadow),0.12)] transition hover:bg-[rgba(var(--subpanel),0.9)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))] ${shouldShowSidebarButton ? '' : 'lg:hidden'}`}
                onClick={toggleSidebar}
                aria-label={isSidebarHoverOpen ? 'Cerrar menu lateral' : 'Abrir menu lateral'}
                aria-expanded={isSidebarHoverOpen}
              >
                <Menu size={17} />
              </button>

              <div className={`flex min-w-0 items-center ${isOperatorCompact ? 'gap-1.5' : 'gap-2'}`}>
                {quickActions}
              </div>
            </div>

            <div className={`flex items-center ${topbarRightGapClassName}`}>
              <IconButton
                onClick={() => setSinaHidden(!isSinaHidden)}
                aria-label={isSinaHidden ? 'Mostrar Sina' : 'Ocultar Sina'}
                title={isSinaHidden ? 'Mostrar Sina' : 'Ocultar Sina'}
                className={iconButtonClassName}
              >
                {isSinaHidden ? <Eye size={17} /> : <EyeOff size={17} />}
              </IconButton>
              <IconButton
                onClick={() => navigate('/chat')}
                aria-label="Abrir chat interno"
                title="Chat interno"
                className={iconButtonClassName}
              >
                <MessageCircleMore size={17} />
                {chatUnreadCount > 0 ? (
                  <span className="pointer-events-none absolute -right-1 -top-1 inline-flex min-w-4 items-center justify-center rounded-full bg-[rgb(var(--brand))] px-1 py-0.5 text-[10px] font-bold text-[rgb(var(--bg))] shadow-[0_8px_18px_rgba(var(--shadow),0.16)]">
                    {chatUnreadCount > 99 ? '99+' : chatUnreadCount}
                  </span>
                ) : null}
              </IconButton>
              <IconButton
                onClick={toggleTheme}
                aria-label="Cambiar tema"
                title="Cambiar tema"
                className={iconButtonClassName}
              >
                {theme === 'light' ? <Moon size={17} /> : <Sun size={17} />}
              </IconButton>
              <NotificationsMenu />
              <UserMenu />
            </div>
          </div>
        </header>
      </div>

      <SearchGuidesModal isOpen={isGuidesModalOpen} onClose={() => setIsGuidesModalOpen(false)} />
      <SearchClientsModal isOpen={isClientsModalOpen} onClose={() => setIsClientsModalOpen(false)} />
    </>
  );
}

